import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  ICON_TYPES,
} from '../../shared/constants';
import { IContentfulLink, IMdBody, ISvgImage } from '../../types/shared';

import PrimaryButton from '../atoms/primary-button/PrimaryButton';

import { DEQ_TYPES } from '../../utils/deq';
import { getCtaLink, ReactMarkdownExtended } from '../../utils/generic';

import mq from '../../styles/breakpoints';
import { Body, H2 } from '../../styles/generic';
import Spacings from '../../styles/Spacings';

import ContentWrapper from '../molecules/content-wrapper/ContentWrapper';

interface Props extends PropsWithChildren {
  title: string;
  description?: IMdBody;
  primaryLink?: IContentfulLink;
  image?: ISvgImage;
}

const ErrorLayout = ({
  title,
  description,
  image,
  primaryLink,
  children,
}: Props) => (
  <Container>
    <HeaderContentWrapper>
      {image && (
        <ImageContainer>
          <img data-testid="image" alt="" src={image.file.url} />
        </ImageContainer>
      )}
      <StyledH2>{title}</StyledH2>
      {description && (
        <Body>
          <ReactMarkdownExtended>
            {description.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        </Body>
      )}
      {primaryLink && (
        <StyledPrimaryButton
          to={getCtaLink(primaryLink)}
          label={primaryLink.label}
          buttonTheme={primaryLink.theme || BUTTON_THEMES.default}
          iconPosition={primaryLink?.iconPosition}
          icon={primaryLink?.icon || ICON_TYPES.none}
          type={primaryLink?.type || BUTTON_TYPES.internal}
          deqComponentTitle={title}
          deqCustomAttributes={{
            type: DEQ_TYPES.textLink,
          }}
        />
      )}
    </HeaderContentWrapper>
    {children && <BodyWrapper>{children}</BodyWrapper>}
  </Container>
);

export default ErrorLayout;

const Container = styled(ContentWrapper)`
  box-sizing: content-box;
  font-family: ${({ theme }) => theme.fontRegular};
  margin: ${Spacings.M} auto 72px;
  max-width: 624px;

  ${mq.from.breakpoint.M`
    margin: ${Spacings.XL} auto 124px;
  `}

  ${mq.from.breakpoint.L`
    margin: 80px auto 116px;
  `}
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  margin-top: ${Spacings.M};
`;

const StyledH2 = styled(H2)`
  margin: ${Spacings.XS} 0;
`;

const ImageContainer = styled.div`
  height: 80px;
  margin: 0 auto;
  width: 80px;
`;

const HeaderContentWrapper = styled.div`
  text-align: center;
`;

const BodyWrapper = styled.div`
  margin-top: ${Spacings.XL};
  width: 100%;
`;
